import api from '@api';
import { call, put, takeEvery, takeLeading, cancelled } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { setLogged } from '../login/reducer';
import {
    getUserFetchSuccess,
    getUserFetchFailure,
    updateUserFetchSuccess,
    updateUserFetchFailure,
    getProviderServiceCodesFetch as getProviderServiceCodesFetchAction,
    getProviderServiceCodesFetchSuccess,
    getProviderServiceCodesFetchFailure,
} from './reducer';
import { ProviderServiceCodesResItemType, UserResType, UserType } from 'src/types/user';
import { initProvider } from '../provider/reducer';
import { initNotificationsConnection } from '../notifications/reducer';
import { initConstants } from '../constantsCodes/reducer';
import translate from '@translate';
import { initUserSettings } from '../userSettings/reducer';
import getFileData from 'src/utils/getFileData';
import { getProvider } from '../provider/functions';
import { bootstrapDebug } from 'src/utils/bootstrapDebug';
import { markPasswordChanged, PASSWORD_CHANGED_RELOGIN_MESSAGE } from 'src/utils/passwordChangedLogout';

const getUserFetchRequest = async () => {
    bootstrapDebug('ui-data:request');
    const res = await api('get', 'api/secure/users/ui-data');
    bootstrapDebug('ui-data:response', { hasPhotoId: Boolean(res.data.PHOTO_ID) });

    if (res.data.PHOTO_ID) {
        try {
            bootstrapDebug('photo:request', { photoId: res.data.PHOTO_ID });
            const photoData = await getFileData(res.data.PHOTO_ID);
            res.data.PHOTO_HASH = photoData;
            bootstrapDebug('photo:done');
        } catch (error) {
            bootstrapDebug('photo:failed', { error: error instanceof Error ? error.message : String(error) });
        }
    }

    return res.data;
};

function* getUserFetch(): SagaIterator {
    bootstrapDebug('getUserFetch:saga:start');
    try {
        const response: UserResType = yield call((): Promise<UserResType> => getUserFetchRequest());
        bootstrapDebug('getUserFetch:ui-data:parsed', {
            providersCount: response.providers?.length ?? 0,
        });

        response.providers = (response.providers || []).sort((a, b) =>
            (a.COMPANY_NAME || '').localeCompare(b.COMPANY_NAME || '')
        );

        const provider = getProvider(response.providers);
        bootstrapDebug('getUserFetch:provider', {
            publicId: provider?.PUBLIC_ID,
            eshopKey: provider?.ESHOP_KEY,
        });

        yield put(initProvider(response.providers));
        bootstrapDebug('getUserFetch:initConstants');
        yield put(initConstants(response.constants ?? {}));
        yield put(initUserSettings(response.userSettings || {}));
        yield put(initNotificationsConnection());
        yield put(setLogged(true));
        yield put(getUserFetchSuccess(response));
        bootstrapDebug('getUserFetch:success');

        if (provider?.PUBLIC_ID) {
            bootstrapDebug('serviceCodes:dispatch', { publicId: provider.PUBLIC_ID });
            yield put(getProviderServiceCodesFetchAction(provider.PUBLIC_ID));
        } else {
            bootstrapDebug('serviceCodes:skipped', { reason: 'missing PUBLIC_ID' });
        }
    } catch (error) {
        bootstrapDebug('getUserFetch:failure', {
            error: error instanceof Error ? error.message : String(error),
        });
        yield put(getUserFetchFailure({ error }));
    } finally {
        if (yield cancelled()) {
            bootstrapDebug('getUserFetch:saga:cancelled');
        }
    }
}

export function* getUserFetchWatch() {
    yield takeLeading('user/getUserFetch', getUserFetch);
}

export const getProviderServiceCodesFetchRequest = async (id: string) => {
    const res = await api('get', `api/secure/providers/serviceCodes/${id}`);
    return res.data?.data ?? [];
};

function* getProviderServiceCodesFetch(action: { type: string; payload: string }) {
    bootstrapDebug('serviceCodes:saga:start', { publicId: action.payload });
    try {
        const response: ProviderServiceCodesResItemType[] = yield call((): Promise<ProviderServiceCodesResItemType[]> =>
            getProviderServiceCodesFetchRequest(action.payload)
        );

        yield put(getProviderServiceCodesFetchSuccess(response));
        bootstrapDebug('serviceCodes:saga:success', { count: response.length });
    } catch (error) {
        bootstrapDebug('serviceCodes:saga:failure', {
            error: error instanceof Error ? error.message : String(error),
        });
        yield put(getProviderServiceCodesFetchFailure({ error }));
    }
}

export function* getProviderServiceCodesFetchWatch() {
    yield takeEvery('user/getProviderServiceCodesFetch', getProviderServiceCodesFetch);
}

const updateUserFetchRequest = async (data: UserType) => {
    const res = await api('put', 'api/secure/users/update-me', { user: data });
    return res.data;
};

function* updateUserFetch(action: { type: string; payload: UserType }) {
    try {
        const response: UserResType = yield call((): Promise<UserResType> => updateUserFetchRequest(action.payload));

        if (response && response.message === PASSWORD_CHANGED_RELOGIN_MESSAGE) {
            // all tokens are revoked from now on; if any request force-logs the user out, login must still show this message
            yield call(markPasswordChanged);
        }

        yield put(
            updateUserFetchSuccess({
                ...response,
                messageSuccess: {
                    title: translate('messages.uploaded'),
                    text: action.payload.PASSWORD && translate('messages.passwordBeenChanged'),
                },
            })
        );
    } catch (error) {
        yield put(updateUserFetchFailure({ error }));
    }
}

export function* updateUserFetchWatch() {
    yield takeEvery('user/updateUserFetch', updateUserFetch);
}
